import React from 'react';
import { useOrganization } from '../../context/OrganizationContext';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import ReadOnlyBanner from './ReadOnlyBanner';

/**
 * OrganizationLayout Component
 *
 * Wrapper for all organization pages that:
 * - Shows loading state while organization data loads
 * - Redirects individual users away from org pages
 * - Displays read-only banner when subscription is inactive
 * - Provides consistent layout structure
 *
 * Usage:
 * <OrganizationLayout>
 *   <YourOrganizationPageContent />
 * </OrganizationLayout>
 */
function OrganizationLayout({ children, requireActive = false }) {
  const navigate = useNavigate();
  const {
    currentOrganization,
    isOrganizationUser,
    subscriptionStatus,
    loading: orgLoading
  } = useOrganization();

  // Show loading state
  if (orgLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  // Redirect individual users to home
  if (!isOrganizationUser) {
    navigate('/');
    return null;
  }

  // No organization selected
  if (!currentOrganization) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <p>No organization selected</p>
      </Box>
    );
  }

  // If requireActive is true and subscription is not active, show only the banner
  // This is useful for pages that should be completely blocked when inactive
  if (requireActive && subscriptionStatus !== 'active' && subscriptionStatus !== 'trialing') {
    return (
      <Box sx={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        <ReadOnlyBanner />
        <Box sx={{ marginTop: 4, textAlign: 'center', color: '#666' }}>
          <p>This feature requires an active subscription.</p>
          <p>Please reactivate your subscription to access this page.</p>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <ReadOnlyBanner />
      {children}
    </>
  );
}

export default OrganizationLayout;
